// Notification Service - Bildirim Yönetimi
import { 
  collection, 
  doc,
  addDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

/**
 * Bildirim tipleri
 */
export const NOTIFICATION_TYPES = {
  NEW_SELLER_APPLICATION: 'new_seller_application',
  NEW_PRODUCT_SUBMISSION: 'new_product_submission',
  PRODUCT_EDIT_REQUEST: 'product_edit_request',
  PRODUCT_DELETED: 'product_deleted',
  NEW_PROFILE_IMAGE: 'new_profile_image',
  SELLER_NAME_CHANGE_REQUEST: 'seller_name_change_request',
  ORDER_UPDATE: 'order_update',
  SALE_COMPLETED: 'sale_completed',
  FEEDBACK: 'feedback',
  // Genel amaçlı bildirim (mağaza daveti, ekip vb.)
  GENERAL: 'general',
  // Admin'in tek kullanıcıya gönderdiği mesaj
  ADMIN_MESSAGE: 'admin_message',
  // Admin toplu duyurusu
  ANNOUNCEMENT: 'announcement',
  // Satıcıya gönderilen bildirimler
  SELLER_APPROVED: 'seller_approved',
  SELLER_REJECTED: 'seller_rejected',
  PRODUCT_APPROVED: 'product_approved',
  PRODUCT_REJECTED: 'product_rejected',
  PRODUCT_EDIT_APPROVED: 'product_edit_approved',
  PRODUCT_EDIT_REJECTED: 'product_edit_rejected',
  PROFILE_IMAGE_APPROVED: 'profile_image_approved',
  PROFILE_IMAGE_REJECTED: 'profile_image_rejected'
};

/**
 * Bildirim durumları
 */
export const NOTIFICATION_STATUS = {
  UNREAD: 'unread',
  READ: 'read',
  ARCHIVED: 'archived',
  DELETED: 'deleted'
};

/**
 * Kullanıcı bildirim ayarları durumları
 */
export const NOTIFICATION_SETTING_STATUS = {
  ACTIVE: 'active',           // Aktif - Tüm bildirimler açık
  DO_NOT_DISTURB: 'dnd',      // Rahatsız etme/Mola - Bildirimler kapalı
  QUIET_HOURS: 'quiet_hours' // Sessiz saatler (gelecekte eklenebilir)
};

/**
 * Yeni bildirim oluşturur
 * @param {string} userId - Bildirim alacak kullanıcı ID
 * @param {string} type - Bildirim tipi (NOTIFICATION_TYPES)
 * @param {string} title - Bildirim başlığı
 * @param {string} message - Bildirim mesajı
 * @param {Object} metadata - Ek bilgiler (link, entityId, vb.)
 * @returns {Promise}
 */
export const createNotification = async (userId, type, title, message, metadata = {}) => {
  try {
    const notificationRef = await addDoc(collection(db, 'notifications'), {
      userId,
      type,
      title,
      message,
      status: NOTIFICATION_STATUS.UNREAD,
      metadata,
      createdAt: serverTimestamp(),
      readAt: null
    });

    logger.log(`✅ Bildirim oluşturuldu: ${notificationRef.id} (${type})`);
    return { success: true, notificationId: notificationRef.id };
  } catch (error) {
    logger.error('❌ Bildirim oluşturulurken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Admin ve moderator'lere bildirim gönderir
 * Kullanıcıların bildirim ayarlarını kontrol eder (Rahatsız Etme modu)
 * @param {string} type - Bildirim tipi
 * @param {string} title - Bildirim başlığı
 * @param {string} message - Bildirim mesajı
 * @param {Object} metadata - Ek bilgiler
 * @returns {Promise}
 */
export const notifyAdminsAndModerators = async (type, title, message, metadata = {}) => {
  try {
    // Admin ve moderator kullanıcıları bul
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    const eligibleUserIds = [];
    usersSnapshot.forEach((doc) => {
      const userData = doc.data();
      const roles = userData.roles || [];
      
      // Admin veya moderator kontrolü
      if (roles.includes('admin') || roles.includes('moderator')) {
        // Bildirim ayarlarını kontrol et
        const notificationSettings = userData.notificationSettings || {
          status: NOTIFICATION_SETTING_STATUS.ACTIVE,
          browserNotifications: true,
          emailNotifications: false
        };
        
        // Eğer kullanıcı "Rahatsız Etme" modunda değilse bildirim gönder
        if (notificationSettings.status !== NOTIFICATION_SETTING_STATUS.DO_NOT_DISTURB) {
          eligibleUserIds.push(doc.id);
        } else {
          logger.log(`⏸️ Kullanıcı ${doc.id} rahatsız etme modunda, bildirim gönderilmedi`);
        }
      }
    });

    // Uygun kullanıcılara bildirim oluştur
    const promises = eligibleUserIds.map(userId => 
      createNotification(userId, type, title, message, metadata)
    );

    await Promise.all(promises);
    logger.log(`✅ ${eligibleUserIds.length} admin/moderator'a bildirim gönderildi`);
    return { success: true, count: eligibleUserIds.length };
  } catch (error) {
    logger.error('❌ Admin/moderator bildirimleri gönderilirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Sadece Admin'lere bildirim gönderir (Yeni satıcı başvurusu, Yapılan satış vb.)
 */
export const notifyAdminsOnly = async (type, title, message, metadata = {}) => {
  try {
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    const eligibleUserIds = [];
    usersSnapshot.forEach((docSnap) => {
      const userData = docSnap.data();
      const roles = userData.roles || [];
      if (!roles.includes('admin')) return;
      const notificationSettings = userData.notificationSettings || { status: NOTIFICATION_SETTING_STATUS.ACTIVE };
      if (notificationSettings.status !== NOTIFICATION_SETTING_STATUS.DO_NOT_DISTURB) {
        eligibleUserIds.push(docSnap.id);
      }
    });
    const promises = eligibleUserIds.map(userId => createNotification(userId, type, title, message, metadata));
    await Promise.all(promises);
    return { success: true, count: eligibleUserIds.length };
  } catch (error) {
    logger.error('❌ Admin bildirimleri gönderilirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Sadece Moderator'lere bildirim gönderir (Satıcı isim değişikliği, Ürün silme vb.)
 */
export const notifyModeratorsOnly = async (type, title, message, metadata = {}) => {
  try {
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    const eligibleUserIds = [];
    usersSnapshot.forEach((docSnap) => {
      const userData = docSnap.data();
      const roles = userData.roles || [];
      if (!roles.includes('moderator')) return;
      const notificationSettings = userData.notificationSettings || { status: NOTIFICATION_SETTING_STATUS.ACTIVE };
      if (notificationSettings.status !== NOTIFICATION_SETTING_STATUS.DO_NOT_DISTURB) {
        eligibleUserIds.push(docSnap.id);
      }
    });
    const promises = eligibleUserIds.map(userId => createNotification(userId, type, title, message, metadata));
    await Promise.all(promises);
    return { success: true, count: eligibleUserIds.length };
  } catch (error) {
    logger.error('❌ Moderator bildirimleri gönderilirken hata:', error);
    return { success: false, error: error.message };
  }
};

const MSG_TITLE_MAX = 120;
const MSG_BODY_MAX = 2000;

/**
 * Admin/moderator tek bir kullanıcıya mesaj gönderir (bildirim olarak düşer).
 * @param {string} userId - Hedef kullanıcı
 * @param {string} title
 * @param {string} message
 * @param {string} adminId - Gönderen admin/moderator uid
 * @returns {Promise<{success:boolean, error?:string}>}
 */
export const sendAdminMessageToUser = async (userId, title, message, adminId = null) => {
  const t = typeof title === 'string' ? title.trim() : '';
  const m = typeof message === 'string' ? message.trim() : '';
  if (!userId) return { success: false, error: 'Hedef kullanıcı yok.' };
  if (!t || !m) return { success: false, error: 'Başlık ve mesaj zorunludur.' };
  return createNotification(
    userId,
    NOTIFICATION_TYPES.ADMIN_MESSAGE,
    t.slice(0, MSG_TITLE_MAX),
    m.slice(0, MSG_BODY_MAX),
    { fromAdmin: adminId, link: '/notifications' }
  );
};

/**
 * Toplu duyuru: hedef kitleye göre tüm kullanıcılara bildirim oluşturur.
 * @param {Object} params
 * @param {'all'|'sellers'|'buyers'|'staff'} [params.audience='all']
 * @param {string} params.title
 * @param {string} params.message
 * @param {string} [params.adminId]
 * @returns {Promise<{success:boolean, count?:number, error?:string}>}
 */
export const sendBulkAnnouncement = async ({ audience = 'all', title, message, adminId = null } = {}) => {
  const t = typeof title === 'string' ? title.trim() : '';
  const m = typeof message === 'string' ? message.trim() : '';
  if (!t || !m) return { success: false, error: 'Başlık ve mesaj zorunludur.' };
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const targets = [];
    usersSnapshot.forEach((docSnap) => {
      const roles = docSnap.data().roles || [];
      let include = false;
      if (audience === 'all') include = true;
      else if (audience === 'sellers') include = roles.includes('seller');
      else if (audience === 'staff') include = roles.includes('admin') || roles.includes('moderator');
      else if (audience === 'buyers') include = !roles.includes('seller') && !roles.includes('admin') && !roles.includes('moderator');
      if (include) targets.push(docSnap.id);
    });

    const payloadTitle = t.slice(0, MSG_TITLE_MAX);
    const payloadBody = m.slice(0, MSG_BODY_MAX);
    const CHUNK = 50;
    let count = 0;
    for (let i = 0; i < targets.length; i += CHUNK) {
      const chunk = targets.slice(i, i + CHUNK);
      await Promise.all(chunk.map((uid) =>
        createNotification(uid, NOTIFICATION_TYPES.ANNOUNCEMENT, payloadTitle, payloadBody, {
          fromAdmin: adminId, link: '/notifications', announcement: true
        })
      ));
      count += chunk.length;
    }
    logger.log(`📢 Duyuru ${count} kullanıcıya gönderildi (${audience})`);
    return { success: true, count };
  } catch (error) {
    logger.error('❌ Toplu duyuru gönderilirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Kullanıcının bildirimlerini getirir
 * @param {string} userId - Kullanıcı ID
 * @param {boolean} unreadOnly - Sadece okunmamış bildirimler
 * @returns {Promise}
 */
export const getUserNotifications = async (userId, unreadOnly = false) => {
  try {
    const notificationsRef = collection(db, 'notifications');
    let q = query(
      notificationsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    if (unreadOnly) {
      q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('status', '==', NOTIFICATION_STATUS.UNREAD),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    const notifications = [];
    snapshot.forEach((doc) => {
      notifications.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, data: notifications };
  } catch (error) {
    logger.error('❌ Bildirimler getirilirken hata:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Bildirimleri real-time dinler
 * @param {string} userId - Kullanıcı ID
 * @param {Function} callback - Callback fonksiyonu
 * @returns {Function} Unsubscribe fonksiyonu
 */
export const subscribeToNotifications = (userId, callback) => {
  const notificationsRef = collection(db, 'notifications');
  // En yeni 50 bildirim yeterli (zil 10, sayfa hepsini gösterir); sınırsız büyümeyi önler.
  const q = query(
    notificationsRef,
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

  return onSnapshot(q, (snapshot) => {
    const notifications = [];
    snapshot.forEach((doc) => {
      notifications.push({
        id: doc.id,
        ...doc.data()
      });
    });
    callback(notifications);
  }, (error) => {
    logger.error('❌ Bildirim dinleme hatası:', error);
    callback([]);
  });
};

/**
 * Bildirimi okundu olarak işaretler
 * @param {string} notificationId - Bildirim ID
 * @returns {Promise}
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      status: NOTIFICATION_STATUS.READ,
      readAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    logger.error('❌ Bildirim okundu işaretlenirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Tüm bildirimleri okundu olarak işaretler
 * @param {string} userId - Kullanıcı ID
 * @returns {Promise}
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const result = await getUserNotifications(userId, true);
    if (!result.success) {
      return result;
    }

    const promises = result.data.map(notification => 
      markNotificationAsRead(notification.id)
    );

    await Promise.all(promises);
    return { success: true };
  } catch (error) {
    logger.error('❌ Tüm bildirimler okundu işaretlenirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Bildirimi arşivler
 * @param {string} notificationId - Bildirim ID
 * @returns {Promise}
 */
export const archiveNotification = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      status: NOTIFICATION_STATUS.ARCHIVED
    });
    return { success: true };
  } catch (error) {
    logger.error('❌ Bildirim arşivlenirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Bildirimi soft-delete yapar (veritabanından silmez, status: 'deleted' olarak işaretler)
 * Raporlama için veri korunur
 * @param {string} notificationId - Bildirim ID
 * @returns {Promise}
 */
export const deleteNotification = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      status: NOTIFICATION_STATUS.DELETED,
      deletedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    logger.error('❌ Bildirim silinirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Kullanıcının bildirim ayarlarını getirir
 * @param {string} userId - Kullanıcı ID
 * @returns {Promise}
 */
export const getNotificationSettings = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { success: false, error: 'Kullanıcı bulunamadı' };
    }

    const userData = userDoc.data();
    const settings = userData.notificationSettings || {
      status: NOTIFICATION_SETTING_STATUS.ACTIVE,
      browserNotifications: true,
      emailNotifications: false
    };

    return { success: true, data: settings };
  } catch (error) {
    logger.error('❌ Bildirim ayarları getirilirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Kullanıcının bildirim ayarlarını günceller
 * @param {string} userId - Kullanıcı ID
 * @param {Object} settings - Yeni ayarlar
 * @returns {Promise}
 */
export const updateNotificationSettings = async (userId, settings) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      notificationSettings: settings,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    logger.error('❌ Bildirim ayarları güncellenirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Chrome bildirim izni ister
 * @returns {Promise<string>} 'granted', 'denied', veya 'default'
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    logger.warn('⚠️ Bu tarayıcı bildirimleri desteklemiyor');
    return 'unsupported';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    logger.error('❌ Bildirim izni istenirken hata:', error);
    return 'error';
  }
};

/**
 * Chrome bildirimi gösterir
 * @param {string} title - Bildirim başlığı
 * @param {Object} options - Bildirim seçenekleri
 * @returns {Notification|null}
 */
export const showBrowserNotification = (title, options = {}) => {
  if (!('Notification' in window)) {
    logger.warn('⚠️ Bu tarayıcı bildirimleri desteklemiyor');
    return null;
  }

  if (Notification.permission !== 'granted') {
    logger.warn('⚠️ Bildirim izni verilmemiş');
    return null;
  }

  const defaultOptions = {
    body: '',
    icon: '/images/3deralogo.png',
    badge: '/images/3deralogo.png',
    tag: '3dera-notification',
    requireInteraction: false,
    ...options
  };

  try {
    const notification = new Notification(title, defaultOptions);
    
    // Bildirime tıklandığında
    notification.onclick = () => {
      window.focus();
      if (options.onClick) {
        options.onClick();
      }
      notification.close();
    };

    // Otomatik kapanma (5 saniye sonra)
    setTimeout(() => {
      notification.close();
    }, 5000);

    return notification;
  } catch (error) {
    logger.error('❌ Bildirim gösterilirken hata:', error);
    return null;
  }
};

/**
 * Okunmamış bildirim sayısını getirir
 * @param {string} userId - Kullanıcı ID
 * @returns {Promise<number>}
 */
export const getUnreadNotificationCount = async (userId) => {
  try {
    const result = await getUserNotifications(userId, true);
    return result.success ? result.data.length : 0;
  } catch (error) {
    logger.error('❌ Okunmamış bildirim sayısı getirilirken hata:', error);
    return 0;
  }
};
